import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { useAuth } from "../../../contexts/AuthContext";
import {
  DELETE_ACCOUNT_CONFIRM_PHRASE,
  markAccountDeleted,
  requestAccountDeletion,
} from "../../../lib/deleteAccount";
import {
  APPLE_SUBSCRIPTIONS_URL,
  PLAY_SUBSCRIPTIONS_URL,
} from "../../../lib/billing";

interface DeleteAccountDialogProps {
  onCancel: () => void;
  storeWarning?: "apple" | "play" | null;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  onCancel,
  storeWarning = null,
}) => {
  const { signOut } = useAuth();
  const [phrase, setPhrase] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openedStore, setOpenedStore] = useState(!storeWarning);

  useEscapeKey(onCancel, { enabled: !isDeleting });

  const canConfirm =
    phrase.trim() === DELETE_ACCOUNT_CONFIRM_PHRASE &&
    !isDeleting &&
    openedStore;

  const handleDelete = async () => {
    if (!canConfirm) return;
    setIsDeleting(true);
    setError(null);
    try {
      await requestAccountDeletion();
      markAccountDeleted();
      await signOut();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not delete your account. Please try again.",
      );
      setIsDeleting(false);
    }
  };

  // Portal to body so the dimming layer covers the viewport. Settings uses a
  // transform animation, which would otherwise trap `position: fixed` and leave
  // the Settings border visible as a frame around a half-dimmed panel.
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40 p-2 sm:p-4 animate-dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isDeleting) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-labelledby="delete-account-title"
        aria-describedby="delete-account-description"
        className="bg-warm-bg-card rounded-xl border border-warm-border p-4 sm:p-6 w-full max-w-md max-h-[95vh] overflow-y-auto animate-dialog-panel"
      >
        <h3
          id="delete-account-title"
          className="text-lg font-semibold text-warm-text-primary mb-3"
        >
          Delete account?
        </h3>
        <p
          id="delete-account-description"
          className="text-warm-text-secondary text-sm mb-4"
        >
          This permanently removes your account, your tasks, lists, files, and
          connected billing. It cannot be undone.
        </p>

        {storeWarning === "apple" ? (
          <div className="mb-4 space-y-3">
            <p className="text-warm-text-secondary text-sm">
              Deleting JuDI does not cancel Apple. Cancel the subscription in
              Apple subscription settings first.
            </p>
            <a
              href={APPLE_SUBSCRIPTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpenedStore(true)}
              className="inline-flex px-4 py-2 text-sm font-medium text-warm-text-secondary bg-warm-bg-elevated hover:bg-warm-border-subtle rounded-lg transition-colors"
            >
              Open Apple subscriptions
            </a>
          </div>
        ) : null}
        {storeWarning === "play" ? (
          <div className="mb-4 space-y-3">
            <p className="text-warm-text-secondary text-sm">
              Deleting JuDI does not cancel Google Play. Cancel the
              subscription in Google Play first.
            </p>
            <a
              href={PLAY_SUBSCRIPTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpenedStore(true)}
              className="inline-flex px-4 py-2 text-sm font-medium text-warm-text-secondary bg-warm-bg-elevated hover:bg-warm-border-subtle rounded-lg transition-colors"
            >
              Open Google Play
            </a>
          </div>
        ) : null}

        <label
          htmlFor="delete-account-confirm"
          className="block text-sm font-medium text-warm-text-primary mb-2"
        >
          Type{" "}
          <span className="font-mono font-semibold">
            {DELETE_ACCOUNT_CONFIRM_PHRASE}
          </span>{" "}
          to confirm
        </label>
        <input
          id="delete-account-confirm"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          disabled={isDeleting}
          className="w-full bg-warm-bg-elevated border border-warm-border rounded-lg px-3 py-2 text-sm text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue mb-4 disabled:opacity-50"
        />

        {error ? (
          <p role="alert" className="text-sm text-warm-text-primary mb-4">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={!canConfirm}
            className="flex-1 px-4 py-2 text-white rounded-lg text-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: "var(--color-recording)" }}
          >
            {isDeleting
              ? "Deleting…"
              : error
                ? "Try again"
                : "Delete account"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DeleteAccountDialog;
